import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Form, Spinner } from "react-bootstrap";

import DateRangeField from "../../common/components/DateRangeField";
import FloatingLabelInput from "../../common/components/FloatingLabelInput";
import FloatingLabelSelect from "../../common/components/FloatingLabelSelect";
import FormSelect from "../../common/components/FormSelect";
import Select, { CheckboxOption } from '../../common/components/Select';
import SideBar, { Body } from "../../common/components/SearchFilters/SideBar";

const FilterSideBar = ({
  filterOptions: initialFilterOptions,
  filters,
  onClose,
  setFilters,
  showSideBar,
  testGroup,
}) => {

  const [fetchCompleted, setFetchCompleted] = useState(false);
  const [fetchedFilterOptions, setFetchedFilterOptions] = useState(initialFilterOptions);

  const fetchSideBarFilterOptions = async () => {
    const response = await axios.get(`/test_groups/${testGroup.slug}/case_reports/fetch_sidebar_filter_options`);
    if (response.status == 200) {
      const fetchedFilterOptions = JSON.parse(response.data.sidebar_filter_options);
      setFetchedFilterOptions({...initialFilterOptions, ...fetchedFilterOptions});
      setFetchCompleted(true);
    }
  };

  // temporary filter state when opening the sidebar
  // sidebarFilter and filters get syncronized when user clicks "Apply filters"
  const [sideBarFilters, setSideBarFilters] = useState(filters);

  useEffect(() => {
    setSideBarFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (showSideBar && !fetchCompleted) {
      fetchSideBarFilterOptions();
    }
  }, [showSideBar]);

  const handleApplyFilters = () => {
    setFilters(sideBarFilters);
    onClose();
  };

  const setSideBarFieldValue = (field, value) => {
    setSideBarFilters({ ...sideBarFilters, [field]: value });
  };

  return (
    <SideBar
      id="caseReportsFiltersSideBar"
      onClose={onClose}
      placement="end"
      show={showSideBar}
      title={`Filters`}
    >
      <Body>
        {!fetchCompleted ? (
          <div className="text-center">
            <Spinner variant="primary" animation="border" />
          </div>
        ) : (
          <div className="d-flex flex-column justify-content-between">
            <div className="my-3">
              <FloatingLabelInput
                label="Search by name or barcode"
                value={sideBarFilters.q}
                onChange={(e) => setSideBarFieldValue("q", e.target.value)}
                className="my-3"
                prependIcon="search"
              />
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Services</Form.Label>
                  <Select
                    isMulti
                    options={fetchedFilterOptions.service_options}
                    className={`service_selector service_selector`}
                    blurInputOnSelect={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable={false}
                    controlShouldRenderValue={false}
                    verticallyCenterCheckBox={true}
                    placeholder={""}
                    components={{ Option: CheckboxOption }}
                    value={sideBarFilters.test_configuration_ids}
                    onChange={(values) => {
                      setSideBarFieldValue(
                        "test_configuration_ids",
                        values
                      )
                    }}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Result</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.result_options}
                    onChange={(result) => setSideBarFieldValue("result", result)}
                    selected={fetchedFilterOptions.result_options?.filter(o => o.value === sideBarFilters.result)}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.status_options}
                    onChange={(testState) => setSideBarFieldValue("test_state", testState)}
                    selected={fetchedFilterOptions.status_options?.filter(o => o.value === sideBarFilters.test_state)}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Date(s) of service</Form.Label>
                  <DateRangeField
                    clearDate={() => setSideBarFilters({...sideBarFilters, start_day: "", end_day: ""})}
                    date_format= "MM/DD"
                    end_day_field_name= "end_day"
                    onChange={(start, end) => setSideBarFilters({...sideBarFilters, start_day: start.format("YYYY-MM-DD"), end_day: end.format("YYYY-MM-DD")})}
                    placeholder=""
                    start_day_field_name= "start_day"
                    values={[sideBarFilters.start_day, sideBarFilters.end_day]}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Resulted by</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.administered_by_options}
                    onChange={(administeredById) => setSideBarFieldValue("resulted_by_id", administeredById)}
                    selected={fetchedFilterOptions.administered_by_options?.filter((option) => option.value === sideBarFilters.resulted_by_id)}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Location</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.location_options}
                    onChange={(id) => setSideBarFieldValue("appointment_slot_group_id", id)}
                    selected={fetchedFilterOptions.location_options?.filter(o => o.value === sideBarFilters.appointment_slot_group_id)}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Case report status</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.case_report_status_options}
                    onChange={(status) => setSideBarFieldValue("case_report_status", status)}
                    selected={fetchedFilterOptions.case_report_status_options?.filter(o => o.value === sideBarFilters.case_report_status)}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Populations</Form.Label>
                  <FloatingLabelSelect
                    value={fetchedFilterOptions.population_options?.filter((option) =>
                      sideBarFilters.populations?.includes(option.value)
                    )}
                    onChange={(s) =>
                      setSideBarFieldValue(
                        "populations",
                        (s || []).map((s) => s.value)
                      )
                    }
                    options={fetchedFilterOptions.population_options}
                    isMulti={true}
                    filledValue={!!sideBarFilters.populations?.length}
                  />
                  </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Published</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.published_options}
                    onChange={(status) => setSideBarFieldValue("published", status)}
                    selected={fetchedFilterOptions.published_options?.filter(o => o.value === sideBarFilters.published)}
                  />
                </Form.Group>
              </div>
              <div className="my-3">
                <Form.Group>
                  <Form.Label>Vaccine manufacturer</Form.Label>
                  <FormSelect
                    options={fetchedFilterOptions.vaccine_manufacturer_options}
                    onChange={(vm) => setSideBarFieldValue("vaccine_route_id", vm)}
                    selected={fetchedFilterOptions.vaccine_manufacturer_options?.filter(o => o.value === sideBarFilters.vaccine_route_id)}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="position-sticky bottom-0">
              <Button
                block
                className="my-3"
                onClick={handleApplyFilters}
              >
                Apply filters
              </Button>
            </div>
          </div>
        )}
      </Body>
    </SideBar>
  );
};

export default FilterSideBar;
